/**
 * What is asked for when a supplier is added or edited.
 *
 * Jonathon, 2026-10-31: "there needs to be an option to edit the suppliers.
 * and when we click on add a new it should have a pop up box with all the info
 * to fill out." Before this, Create wrote a row called "New supplier" — whose
 * name could then never be changed, because supplier names are locked — and
 * left the rest to be filled in cell by cell along a table too wide for a
 * phone.
 *
 * The same form serves both. Adding asks for the name; editing shows it and
 * does not change it (rule 8 — material orders and documents carry the name as
 * text, so renaming one orphans every row that says the old name).
 */

import { looksLikeEmailAddress } from "./gmail.js";
import type { Supplier } from "./types.js";

/** A supplier as the form holds it, before anything is written. */
export interface SupplierDraft {
  name: string;
  abbr: string;
  primary_supplier_contact: string;
  /** Where Place Material Order sends to. */
  email: string;
  /** The price sheet this supplier is quoted off. "" is the catalogue cost. */
  price_list_id: string;
  /** Invoiced later rather than paid at the till (ADR 0111). */
  on_account: boolean;
  active: boolean;
  /** Credits our account for a QuickMeasure report (ADR 0110). */
  credits_quickmeasure: boolean;
}

export const blankSupplierDraft = (): SupplierDraft => ({
  name: "",
  abbr: "",
  primary_supplier_contact: "",
  email: "",
  price_list_id: "",
  on_account: false,
  active: true,
  credits_quickmeasure: false,
});

/** The form, filled in with a supplier that already exists. */
export function supplierDraftFrom(supplier: Supplier): SupplierDraft {
  return {
    name: supplier.name,
    abbr: supplier.abbr ?? "",
    primary_supplier_contact: supplier.primary_supplier_contact ?? "",
    email: supplier.email ?? "",
    price_list_id: supplier.price_list_id ?? "",
    on_account: supplier.on_account ?? false,
    active: supplier.active ?? true,
    credits_quickmeasure: supplier.credits_quickmeasure ?? false,
  };
}

/**
 * Why this supplier cannot be saved yet, in words the office can act on.
 *
 * `existing` is every supplier name in the table, active or not — names are
 * unique (`suppliers_name_key`) and the database's own refusal names the
 * constraint, not the supplier in the way. Pass `editing` with the supplier's
 * own name when editing, so it does not collide with itself.
 */
export function supplierDraftProblem(
  draft: SupplierDraft,
  existing: Iterable<string | null | undefined>,
  editing?: string,
): string | null {
  const name = draft.name.trim();
  if (name === "") return "Give the supplier a name.";

  if (editing === undefined) {
    for (const taken of existing) {
      if (taken && taken.trim().toLowerCase() === name.toLowerCase()) {
        return `There is already a supplier called ${taken.trim()}.`;
      }
    }
  }

  // Material orders are emailed here, and a typo goes nowhere without a word.
  // The field may hold more than one address, split the way inbox sender
  // matching splits it (`senderMatchesSupplier`): commas, semicolons or spaces.
  const email = draft.email.trim();
  if (email !== "") {
    // The same shape check the send path uses, so the form never accepts an
    // address that sending would then refuse.
    const isAddress = looksLikeEmailAddress;
    const pieces = email.split(/[\s,;]+/).filter((piece) => piece !== "");
    const bad = pieces.find((piece) => !isAddress(piece));
    if (bad !== undefined) {
      // With no address in it at all, the whole field is what is wrong.
      const named = pieces.some(isAddress) ? bad : email;
      return `"${named}" is not an email address.`;
    }
  }

  return null;
}

/**
 * The columns the form writes, once there is no problem with it.
 *
 * Empty boxes become null rather than "": a supplier with an order email of ""
 * looks like one with an address everywhere that checks.
 */
export function supplierDraftFields(draft: SupplierDraft): {
  abbr: string | null;
  primary_supplier_contact: string | null;
  email: string | null;
  price_list_id: string | null;
  on_account: boolean;
  active: boolean;
  credits_quickmeasure: boolean;
} {
  const tidy = (value: string): string | null => value.trim() || null;
  return {
    abbr: tidy(draft.abbr),
    primary_supplier_contact: tidy(draft.primary_supplier_contact),
    email: tidy(draft.email),
    price_list_id: draft.price_list_id || null,
    on_account: draft.on_account,
    active: draft.active,
    credits_quickmeasure: draft.credits_quickmeasure,
  };
}

/** The row to insert for a new supplier: the fields, and its name. */
export function supplierDraftRow(
  draft: SupplierDraft,
): ReturnType<typeof supplierDraftFields> & { name: string } {
  return { name: draft.name.trim(), ...supplierDraftFields(draft) };
}
